// Share a "stat card" — renders a beautiful PNG via <canvas> and fires Web Share API.
// Falls back to download-as-PNG if the platform doesn't support sharing files.
//
// Usage: await shareStat({ title, value, subtitle, accent, brand })

const wrap = (ctx, text, maxWidth) => {
  const words = String(text || "").split(" ");
  const lines = [];
  let current = "";
  for (const w of words) {
    const next = current ? current + " " + w : w;
    if (ctx.measureText(next).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
};

export async function shareStat({ title = "Omega ZI AI", value = "", subtitle = "", accent = "#38bdf8", brand = "Omega ZI AI 6.0" }) {
  const W = 1080, H = 1080; // 1:1 for universal sharing
  const cvs = document.createElement("canvas");
  cvs.width = W; cvs.height = H;
  const ctx = cvs.getContext("2d");

  // Solid navy background (brand)
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, "#05070d");
  grad.addColorStop(1, "#0a1b3a");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Accent strip top
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, W, 10);

  // Brand top-left
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "600 30px Outfit, system-ui, sans-serif";
  ctx.fillText(brand, 64, 80);

  // Dot
  ctx.fillStyle = accent;
  ctx.beginPath(); ctx.arc(W - 84, 72, 10, 0, Math.PI * 2); ctx.fill();

  // Title (category)
  ctx.fillStyle = accent;
  ctx.font = "700 32px Outfit, system-ui, sans-serif";
  ctx.fillText(String(title).toUpperCase(), 64, 260);

  // Big value
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 190px Outfit, system-ui, sans-serif";
  ctx.fillText(String(value), 56, 480);

  // Subtitle — wrapped
  ctx.fillStyle = "#cbd5e1";
  ctx.font = "500 36px Outfit, system-ui, sans-serif";
  const subLines = wrap(ctx, subtitle, W - 128);
  subLines.slice(0, 4).forEach((ln, i) => ctx.fillText(ln, 64, 560 + i * 50));

  // Footer tag
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = "500 26px Outfit, system-ui, sans-serif";
  ctx.fillText("Trade like a machine · Win like a quant.", 64, H - 80);

  // Produce blob
  const blob = await new Promise(res => cvs.toBlob(res, "image/png", 0.95));
  if (!blob) return false;
  const file = new File([blob], "omega-stat.png", { type: "image/png" });

  // Try native share with file
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: `${title} · ${value}`, text: `${title}: ${value}${subtitle ? " — " + subtitle : ""}` });
      return true;
    } catch { /* user cancelled — fall through to download */ }
  }

  // Fallback — trigger download
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "omega-stat.png";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return true;
}
